import React, { useEffect, useState } from 'react';

import { CTA, HandleTextInputEvent, InputAppearanceValue, ThemeProvider } from '@vw-marketing/us-components';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { NoTranslateTextInput } from '../../../../shared/components/no-translate-text-input';
import { StyledNicknameFormWrapper } from './styled';
import { FeatureAppTrackingManager } from '../../../../shared/typings/tagging';
import { onVWBasicStepLoadEvent, onVWBasicButtonClickWithProcessEvent } from '../../../../shared/tagging/tagging-helpers';

export interface CarNicknameFormViewProps {
  translationHandler: (key: string) => string;
  trackingManager: FeatureAppTrackingManager;
  userPreferredVehicle: any;
  updateVehicleNickname: (nickname: string) => Promise<void>;
  isUpdatingNickname: boolean;
  onSuccess: () => void;
  onError: () => void;
  onChange: () => void;
}

export const CarNicknameFormView = ({
  translationHandler,
  trackingManager,
  userPreferredVehicle,
  updateVehicleNickname,
  isUpdatingNickname,
  onSuccess,
  onError,
  onChange,
}: CarNicknameFormViewProps) => {
  const [_, setIsTouched] = useState<boolean>(false);
  const [showMaxLengthError, setShowMaxLengthError] = useState<boolean>(false);

  const currentNickname = userPreferredVehicle?.vehicleNickname || '';

  useEffect(() => {
    try {
      onVWBasicStepLoadEvent(trackingManager, {
        Name: 'Vehicle Management',
        StepName: 'Vehicle Details - Nickname',
        StepNumber: '5.1',
      });
    } catch (error) {
      console.warn('Error tracking nickname interaction layer load:', error);
    }
  }, [trackingManager]);

  const validationSchema = Yup.object({
    nickname: Yup.string()
      .max(30, translationHandler('vehicleManagement.nickname.error.maxLength'))
      .test(
        'no-only-whitespace',
        translationHandler('vehicleManagement.nickname.error.whitespace'),
        function (value) {
          if (!value || value.length === 0) return true;
          return value.trim().length > 0;
        },
      ),
  });

  const formik = useFormik({
    initialValues: { nickname: currentNickname },
    enableReinitialize: true,
    onSubmit: async (values) => {
      const invalidChars = /[<>\"'&]/;
      if (invalidChars.test(values.nickname)) {
        formik.setFieldError(
          'nickname',
          translationHandler('vehicleManagement.nickname.error.invalidChars'),
        );
        return;
      }

      try {
        const nicknameValue = values.nickname.trim();
        await updateVehicleNickname(nicknameValue);
        onSuccess();
      } catch (error) {
        onError();
      }
    },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema,
  });

  const handleFormClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleBlur: HandleTextInputEvent = (e) => {
    e.stopPropagation();
    formik.setFieldTouched('nickname', true);
    formik.handleBlur(e);
    setIsTouched(true);
  };

  const handleChange: HandleTextInputEvent = (e) => {
    e.stopPropagation();
    const value = e.currentTarget.value;
    formik.setFieldValue('nickname', value);

    if (value.length === 30) {
      setShowMaxLengthError(true);
    } else {
      setShowMaxLengthError(false);
    }
  };

  const handleSubmit = (e: any) => {
    e.stopPropagation();

    try {
      onVWBasicButtonClickWithProcessEvent(trackingManager, {
        Name: 'Vehicle Management',
        StepName: 'Vehicle Details - Nickname',
        StepNumber: '5.1',
      }, {
        name: 'Add Nickname',
      });
    } catch (error) {
      console.warn('Error tracking add nickname button click:', error);
    }

    formik.handleSubmit(e);
  };

  useEffect(() => {
    if (formik.dirty && onChange) {
      onChange();
    }
  }, [formik.values, onChange]);

  const hasNicknameChanged = formik.values.nickname.trim() !== currentNickname;
  const isFormValid = formik.isValid && hasNicknameChanged;
  const isButtonDisabled = !isFormValid || isUpdatingNickname;

  const displayError = showMaxLengthError
    ? translationHandler('vehicleManagement.nickname.error.maxLength')
    : (formik.touched.nickname && formik.errors.nickname ? formik.errors.nickname as string : '');

  const hasError = Boolean(displayError);

  return (
    <ThemeProvider theme="main">
      <div
        onClick={handleFormClick}
        onMouseDown={handleFormClick}
        onMouseUp={handleFormClick}
      >
        <form onSubmit={handleSubmit}>
          <StyledNicknameFormWrapper>
            <div
              onClick={handleFormClick}
              onMouseDown={handleFormClick}
              onMouseUp={handleFormClick}
            >
              <NoTranslateTextInput
                selectors={['p']}
                aria-label={translationHandler('vehicleManagement.nickname.title')}
                clearIconLabel="Clear"
                aria-required="false"
                characterCountLimit={30}
                maxLength={30}
                inputMode="text"
                appearance={hasError ? ('error' as InputAppearanceValue) : undefined}
                label={translationHandler('vehicleManagement.nickname.title')}
                message={displayError}
                name="nickname"
                onBlur={handleBlur}
                onChange={handleChange}
                required={false}
                type="text"
                value={formik.values.nickname}
              />
            </div>
            <CTA
              aria-label="Save nickname"
              emphasis="primary"
              stretchContent
              tag="button"
              type="submit"
              disabled={isButtonDisabled}
            >
              {userPreferredVehicle?.vehicleNickname
                ? translationHandler('vehicleManagement.nickname.edit.cta.label')
                : translationHandler('vehicleManagement.nickname.add.cta.label')}
            </CTA>
          </StyledNicknameFormWrapper>
        </form>
      </div>
    </ThemeProvider>
  );
};
